<template>
  <validation-observer ref="form" v-slot="{ invalid }">
    <v-toolbar flat>
      <v-toolbar-title class="text-h5"
        >{{ title }}
        <v-chip v-if="Boolean(appointment)" small class="mx-3"
          >{{ appointment.status }}
        </v-chip>
      </v-toolbar-title>
    </v-toolbar>
    <v-card outlined>
      <v-card-title class="text-subtitle-2">
        <v-icon class="mr-2">mdi-account-outline</v-icon> Profile
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="2" v-if="Boolean(appointment)">
            <v-text-field
              v-model="form.idepClientId"
              label="IDEP Client ID"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <validation-provider rules="required" v-slot="{ errors }">
              <v-text-field
                v-model="form.firstName"
                label="First Name"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="12" sm="4">
            <validation-provider rules="required" v-slot="{ errors }">
              <v-text-field
                v-model="form.lastName"
                label="Last Name"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="pt-4">
              <date-picker
                :field="{ label: 'Date Of Birth' }"
                :value="form.dateOfBirth"
                :validations="{ required: true, maxDate: new Date() }"
                @fieldChange="form.dateOfBirth = $event"
              >
              </date-picker>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="form.streetAddress"
              label="Street Address"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="form.streetAddressLine2"
              label="Street Address Line 2"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-text-field v-model="form.city" label="City"></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              :items="usStates"
              v-model="form.state"
              label="State"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="1">
            <v-text-field
              v-model="form.zipCode"
              label="Zip Code"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="4">
            <validation-provider rules="email" v-slot="{ errors }">
              <v-text-field
                v-model="form.emailAddress"
                label="Email Address"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="phoneNumber"
              label="Phone Number"
              v-mask="phoneMask"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-checkbox
              v-model="form.canReceiveText"
              label="Can Receive Text"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="form.otherLanguageId"
              label="Other Language"
              :items="languages"
              item-text="name"
              item-value="languageId"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-title class="text-subtitle-2">
        <v-icon class="mr-2">mdi-calendar-outline</v-icon> Scheduling
      </v-card-title>
      <v-card-text>
        <v-row class="d-flex justify-center">
          <v-col cols="12" sm="3">
            <date-picker
              :field="{ label: 'Appointment Date' }"
              :value="form.appointmentDate"
              :validations="{ required: true }"
              @fieldChange="form.appointmentDate = $event"
            >
            </date-picker>
          </v-col>
          <v-col cols="12" sm="3">
            <validation-provider rules="required" v-slot="{ errors }">
              <v-text-field
                v-model="form.appointmentTime"
                label="Appointment Time"
                type="time"
                :error-messages="errors"
                dense
              >
              </v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-title class="text-subtitle-2">
        <v-icon class="mr-2">mdi-comment-outline</v-icon> Comments
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-textarea v-model="form.comments" auto-grow rows="2" solo flat>
          </v-textarea>
        </v-row>
      </v-card-text>
      <template v-if="Boolean(appointment)">
        <v-divider></v-divider>
        <v-card-title class="text-subtitle-2">
          <v-icon class="mr-2">mdi-clipboard-check-outline</v-icon>
          Recommendation
        </v-card-title>
        <v-card-subtitle>
          <v-switch v-model="noShow" label="No Show" class="mx-4"></v-switch>
        </v-card-subtitle>
        <v-card-text v-if="!noShow">
          <v-row dense>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="form.arrivalTime"
                label="Arrival Time"
                type="time"
                dense
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="3" dense>
              <v-text-field
                v-model="form.endTime"
                label="End Time"
                type="time"
                dense
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="form.assessorId"
                label="Assessor"
                :items="assessorOptions"
                :item-text="(item) => `${item.firstName} ${item.lastName}`"
                item-value="providerId"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="3">
              <v-select
                v-model="form.recommendation"
                label="Recommendation"
                :items="recommendationOptions"
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" sm="3">
              <date-picker
                :field="{ label: 'IDEP Expiration Date' }"
                :value="form.idepExpirationDate"
                @fieldChange="form.idepExpirationDate = $event"
              >
              </date-picker>
            </v-col>
          </v-row>
        </v-card-text>
      </template>
    </v-card>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        :dark="!loading"
        :disabled="loading"
        text
        @click="clear"
        >Cancel</v-btn
      >
      <v-btn
        color="primary"
        :dark="!loading && !invalid"
        :disabled="loading || invalid"
        text
        @click="save"
        >Save</v-btn
      >
    </v-card-actions>
  </validation-observer>
</template>

<script>
import Appointment from "../../objs/Appointment";
import { mask } from "vue-the-mask";
export default {
  props: ["appointment"],
  directives: {
    mask,
  },
  data() {
    return {
      loading: false,
      form: this.appointment
        ? new Appointment({ ...this.appointment })
        : new Appointment(),
      phoneNumber: this.appointment?.phoneNumber || "",
      phoneMask: "(###) ###-####",
      noShow: this.appointment?.status == "No Show",
    };
  },
  mounted() {
    if (this.appointment) {
      this.form.dateOfBirth = this.appointment?.dateOfBirth
        ? new Date(this.appointment?.dateOfBirth)
        : null;
      this.form.appointmentDate = this.appointment?.appointmentDate
        ? new Date(this.appointment?.appointmentDate)
        : null;
      this.form.idepExpirationDate = this.appointment?.idepExpirationDate
        ? new Date(this.appointment?.idepExpirationDate)
        : null;
    }
  },
  computed: {
    title() {
      return Boolean(this.appointment) ? "Edit Appointment" : "New Appointment";
    },
    languages() {
      return this.$store.state.optionsModule.languages;
    },
    usStates() {
      return this.$store.state.optionsModule.usStates;
    },
    recommendationOptions() {
      return this.$store.state.optionsModule.idepRecommendations;
    },
    assessorOptions() {
      return this.$store.state.optionsModule.assessors.sort((a, b) =>
        a.lastName.localeCompare(b.lastName)
      );
    },
    unmaskedPhoneNumber() {
      return this.phoneNumber.replace(/\D/g, "");
    },
  },
  methods: {
    async save() {
      this.loading = true;

      var message;
      if (Boolean(this.appointment)) {
        message = await this.$store.dispatch(
          "appointmentsModule/updateAppointment",
          {
            ...this.form,
            phoneNumber: this.unmaskedPhoneNumber,
            noShow: this.noShow,
          }
        );
      } else {
        message = await this.$store.dispatch(
          "appointmentsModule/createAppointment",
          {
            ...this.form,
            phoneNumber: this.unmaskedPhoneNumber,
            noShow: this.noShow,
          }
        );
      }

      this.loading = false;
      if (message) {
        this.$store.commit("uxModule/setSnackbarMsg", message);
        this.$store.commit("uxModule/setShowSnackbar", true);
      } else {
        this.clear();
      }
    },
    clear() {
      this.$router.push({ name: "Appointments" });
    },
  },
};
</script>
